import type { Grant } from "./grants.types";

export const GRANTS: Grant[] = [
  {
    id: "baby_quest",
    name: "Baby Quest Foundation",
    organization: "Baby Quest Foundation",
    website: "https://www.babyquestfoundation.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=babyquestfoundation.org&sz=64",
    shortDescription:
      "Grants covering up to one full IVF cycle for individuals and couples who cannot afford fertility treatment.",
    longDescription:
      "The Baby Quest Foundation provides financial assistance to those who cannot afford the high costs of procedures such as IVF, gestational surrogacy, egg and sperm donation, egg freezing, and embryo donation. Founded by Pamela Hirsch, the organization has awarded over 250 grants since its inception. Grants are evaluated based on financial need, medical history, and personal essays.",
    awardAmount: "$2,000–$16,000",
    treatmentsCovered: [
      "IVF",
      "Egg Freezing",
      "Donor Egg",
      "Surrogacy",
      "Embryo Adoption",
    ],
    eligibility: {
      location: "national",
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "any",
      specialGroups: null,
    },
    applicationFee: 75,
    applicationDeadline: "March 12, 2026",
    applicationStatus: "closed",
    frequency: "Annual (January–March)",
    howToApply:
      "Submit online application at babyquestfoundation.org during the open period. Applications require a personal essay, financial documentation, and a physician referral letter.",
    requirements: [
      "Permanent U.S. resident",
      "Under care of accredited U.S. fertility facility",
      "Adequate living arrangements (lease or own residence)",
      "At least one partner maintains employment",
      "Basic health insurance with prenatal coverage",
      "Physician referral letter",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "cade_foundation",
    name: "The Cade Foundation",
    organization: "Tinina Q. Cade Foundation",
    website: "https://www.cadefoundation.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=cadefoundation.org&sz=64",
    shortDescription:
      "Family-building grants of up to $10,000 for fertility treatment or adoption, with a focus on underserved communities.",
    longDescription:
      "The Cade Foundation offers family-building grants to help those struggling with infertility. Founded in honor of Tinina Q. Cade, the organization awards grants of up to $10,000 to offset the cost of fertility treatments or adoption. The foundation also offers a separate medication grant program. They prioritize applicants from underserved communities and those with demonstrated financial need.",
    awardAmount: "Up to $10,000",
    treatmentsCovered: [
      "IVF",
      "IUI",
      "Egg Freezing",
      "Donor Egg",
      "Surrogacy",
      "Adoption",
    ],
    eligibility: {
      location: "national",
      incomeMax: 150000,
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "infertility",
      specialGroups: null,
    },
    applicationFee: 50,
    applicationDeadline: "February 1, 2026",
    applicationStatus: "closed",
    frequency: "Annual",
    howToApply:
      "Apply online at cadefoundation.org. Application includes financial documentation, personal essay, and medical records confirming infertility diagnosis.",
    requirements: [
      "U.S. citizen or legal permanent resident",
      "Diagnosed infertility",
      "Household income under $150,000",
      "Personal essay",
      "Tax returns for financial verification",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "pay_it_forward",
    name: "Pay It Forward Fertility",
    organization: "Pay It Forward Fertility Foundation",
    website: "https://www.payitforwardfertility.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=payitforwardfertility.org&sz=64",
    shortDescription:
      "Community-funded grants for IVF, donor egg IVF, and embryo adoption. Requires no existing fertility insurance coverage.",
    longDescription:
      "Pay It Forward Fertility Foundation is funded by previous fertility patients who want to help others on their journey. The foundation awards grants toward fertility treatments including IVF, IVF using donor eggs, and embryo adoption. Fertility medication is a separate expense not covered by the grant. Awarded funds must be used within 12 months from the date of the award.",
    awardAmount: "Varies",
    treatmentsCovered: ["IVF", "Donor Egg", "Embryo Adoption"],
    eligibility: {
      location: "national",
      ageMax: 40,
      insuranceRequired: "no_fertility",
      citizenshipRequired: "us_citizen",
      maritalStatus: "any",
      diagnosis: "infertility",
      specialGroups: null,
    },
    applicationFee: 50,
    applicationDeadline: null,
    applicationStatus: "open",
    frequency: "Rolling",
    howToApply:
      "Submit application online at payitforwardfertility.org. Requires physician recommendation, financial documentation, and proof of medical insurance.",
    requirements: [
      "U.S. citizen or permanent resident",
      "Female partner under 40 (unless using donor eggs)",
      "No fertility/IVF insurance coverage",
      "Physician recommendation",
      "Proof of medical insurance",
      "Financial documentation showing ability to support a child",
      "Treatment at SART-member clinic",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "bundle_of_joy",
    name: "Bundle of Joy Fund",
    organization: "Kyle and Samantha Busch Foundation",
    website: "https://www.bundleofjoyfund.com",
    favicon:
      "https://www.google.com/s2/favicons?domain=bundleofjoyfund.com&sz=64",
    shortDescription:
      "IVF assistance for patients in North and South Carolina through partnering REACH Fertility clinics.",
    longDescription:
      "The Kyle and Samantha Busch Bundle of Joy Fund provides financial assistance for IVF treatments. Founded by NASCAR personality Samantha Busch from her own fertility journey, the fund partners with REACH Fertility Clinic of Charlotte to provide grants. Applicants must be patients of the partnering clinic and residents of North or South Carolina.",
    awardAmount: "IVF cycle assistance",
    treatmentsCovered: ["IVF"],
    eligibility: {
      location: ["NC", "SC"],
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "infertility",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "open",
    frequency: "Rolling",
    howToApply:
      "Apply through REACH Fertility Clinic of Charlotte. Must be an existing patient of the partnering clinic.",
    requirements: [
      "Legal permanent U.S. resident",
      "Resident of North or South Carolina",
      "Infertility diagnosis from REACH Fertility Clinic",
      "Must be patient of partnering clinic",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "hope_for_fertility",
    name: "Hope for Fertility",
    organization: "Hope for Fertility Foundation",
    website: "https://www.hopeforfertility.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=hopeforfertility.org&sz=64",
    shortDescription:
      "Grants from $250 to $10,000 for legally married U.S. couples with physician-confirmed infertility.",
    longDescription:
      "The Hope for Fertility Foundation provides grants ranging from $250 to $10,000 for fertility treatments. The foundation has two application periods per year and requires applicants to be legally married. A physician must confirm the infertility diagnosis. All treatment must take place within the United States.",
    awardAmount: "$250–$10,000",
    treatmentsCovered: ["IVF", "IUI"],
    eligibility: {
      location: "national",
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "married",
      diagnosis: "infertility",
      specialGroups: null,
    },
    applicationFee: 50,
    applicationDeadline: "March 31, 2026",
    applicationStatus: "open",
    frequency: "Biannual (Jan–Mar, Jul–Sep)",
    howToApply:
      "Apply online at hopeforfertility.org during open application periods. Requires physician confirmation of infertility diagnosis.",
    requirements: [
      "U.S. citizen or permanent resident",
      "Legally married",
      "Physician-confirmed infertility diagnosis",
      "Treatment within the United States",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "gift_of_parenthood",
    name: "Gift of Parenthood",
    organization: "Gift of Parenthood",
    website: "https://www.giftofparenthood.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=giftofparenthood.org&sz=64",
    shortDescription:
      "Up to $25,000 in fertility grants with four giveaways annually. Open to U.S. and Canadian residents.",
    longDescription:
      "Gift of Parenthood offers grants of up to $25,000 to help cover the costs of IVF, egg donation, and other fertility expenses. The organization holds four giveaways annually and is open to legal residents of the United States, District of Columbia, and Canada. This is one of the largest single-grant amounts available.",
    awardAmount: "Up to $25,000",
    treatmentsCovered: ["IVF", "Donor Egg"],
    eligibility: {
      location: "national",
      insuranceRequired: "any",
      citizenshipRequired: "us_or_canada",
      maritalStatus: "any",
      diagnosis: "any",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: "April 5, 2026",
    applicationStatus: "open",
    frequency: "Quarterly (4 giveaways/year)",
    howToApply:
      "Apply online at giftofparenthood.org. Registration and application are free.",
    requirements: ["Legal resident of U.S., D.C., or Canada"],
    lastVerified: "2026-03-18",
  },
  {
    id: "parental_hope",
    name: "Parental Hope",
    organization: "Parental Hope Family Grants",
    website: "https://www.parentalhope.org",
    favicon: "https://www.google.com/s2/favicons?domain=parentalhope.org&sz=64",
    shortDescription:
      "Full IVF cycle or FET grants for ages 21–40 at the Institute for Reproductive Health in Cincinnati, Ohio.",
    longDescription:
      "Parental Hope provides grants for a standard IVF cycle or Frozen Embryo Transfer at the Institute for Reproductive Health in Cincinnati, Ohio. The grant does not cover medication costs or egg donor fees. Applicants must be between 21 and 40 years old and have a documented infertility diagnosis.",
    awardAmount: "Full IVF cycle or FET",
    treatmentsCovered: ["IVF"],
    eligibility: {
      location: ["OH"],
      ageMin: 21,
      ageMax: 40,
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "infertility",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: "Spring 2026",
    applicationStatus: "upcoming",
    frequency: "Annual",
    howToApply:
      "Apply online at parentalhope.org when applications open. Treatment must be at the Institute for Reproductive Health in Cincinnati.",
    requirements: [
      "U.S. citizen or legal resident",
      "Ages 21–40",
      "Infertility diagnosis",
      "Treatment at Institute for Reproductive Health, Cincinnati",
      "Medication costs not covered",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "bivf_foundation",
    name: "BIVF Foundation",
    organization: "BIVF Foundation",
    website: "https://www.bivffoundation.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=bivffoundation.org&sz=64",
    shortDescription:
      "Donor egg IVF and egg freezing grants for patients in New England (MA, ME, NH, RI). Age 48 or younger.",
    longDescription:
      "The BIVF Foundation provides grants for donor egg IVF and egg freezing cycles to patients in the New England area. Grants can be used at any qualified IVF center in Massachusetts, Maine, New Hampshire, or Rhode Island. Applicants for donor egg IVF must be 48 years or younger. The foundation also focuses on oncofertility patients.",
    awardAmount: "IVF or egg freezing cycle",
    treatmentsCovered: ["IVF", "Egg Freezing", "Donor Egg"],
    eligibility: {
      location: ["MA", "ME", "NH", "RI"],
      ageMax: 48,
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "any",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: "March 10, 2026",
    applicationStatus: "closed",
    frequency: "Annual",
    howToApply:
      "Apply online at bivffoundation.org. Must use a qualified IVF center in MA, ME, NH, or RI.",
    requirements: [
      "Patient in New England (MA, ME, NH, RI)",
      "Age 48 or younger for donor egg applicants",
      "Treatment at qualified IVF center in eligible states",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "starfish_infertility",
    name: "Starfish Infertility Foundation",
    organization: "Starfish Infertility Foundation",
    website: "https://www.starfishinfertilityfoundation.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=starfishinfertilityfoundation.org&sz=64",
    shortDescription:
      "Up to $5,000 Braxton Grants for couples without insurance support at SART-member clinics.",
    longDescription:
      "The Starfish Infertility Foundation offers Braxton Grants of up to $5,000 to help couples struggling with infertility. The foundation focuses on applicants who do not have insurance support for fertility treatments. Treatment must be at a SART-member clinic within the United States.",
    awardAmount: "Up to $5,000",
    treatmentsCovered: ["IVF", "IUI"],
    eligibility: {
      location: "national",
      insuranceRequired: "no_fertility",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "infertility",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "closed",
    frequency: "Periodic",
    howToApply:
      "Check starfishinfertilityfoundation.org for application openings. Must use a SART-member clinic.",
    requirements: [
      "U.S. resident",
      "No insurance support for fertility treatment",
      "Treatment at SART-member clinic",
      "Infertility diagnosis",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "hopeful_mama",
    name: "Hopeful Mama Foundation",
    organization: "Hopeful Mama Foundation",
    website: "https://www.hopefulmamafoundation.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=hopefulmamafoundation.org&sz=64",
    shortDescription:
      "Grants from $500 to $5,000 for U.S. residents with ASRM-defined infertility and basic health insurance.",
    longDescription:
      "The Hopeful Mama Foundation provides grants ranging from $500 to $5,000 for fertility treatments. Applicants must have an ASRM-defined infertility diagnosis and basic medical insurance with prenatal coverage. The foundation works to make fertility treatment more accessible for those facing financial barriers.",
    awardAmount: "$500–$5,000",
    treatmentsCovered: ["IVF", "IUI"],
    eligibility: {
      location: "national",
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "infertility",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: "April 1, 2026",
    applicationStatus: "upcoming",
    frequency: "Annual",
    howToApply:
      "Apply online at hopefulmamafoundation.org when applications open. Must work with a U.S. fertility clinic.",
    requirements: [
      "U.S. resident",
      "ASRM-defined infertility diagnosis",
      "Basic medical insurance with prenatal coverage",
      "Treatment at U.S. fertility clinic",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "journey_to_parenthood",
    name: "Journey to Parenthood",
    organization: "Journey to Parenthood",
    website: "https://www.journeytoparenthood.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=journeytoparenthood.org&sz=64",
    shortDescription:
      "Minimum $10,000 grants for IVF, egg donation, and other infertility treatments. 1–4 grants awarded annually.",
    longDescription:
      "Journey to Parenthood awards a minimum of one $10,000 grant annually, with up to four grants given each year. Grants cover IVF, egg donation, and other infertility treatments. Applicants must be U.S. citizens under the care of an infertility specialist.",
    awardAmount: "$10,000+",
    treatmentsCovered: ["IVF", "Donor Egg"],
    eligibility: {
      location: "national",
      insuranceRequired: "any",
      citizenshipRequired: "us_citizen",
      maritalStatus: "any",
      diagnosis: "infertility",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: "August 1 – October 12, 2026",
    applicationStatus: "upcoming",
    frequency: "Annual (Aug–Oct)",
    howToApply:
      "Apply online at journeytoparenthood.org during the application window.",
    requirements: [
      "U.S. citizen living in the United States",
      "Under care of infertility specialist",
      "Infertility diagnosis",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "cozy_warrior",
    name: "The Cozy Warrior Grant",
    organization: "Cozy Warrior",
    website: "https://www.cozywarrior.org",
    favicon: "https://www.google.com/s2/favicons?domain=cozywarrior.org&sz=64",
    shortDescription:
      "$5,000 grants for fertility treatment, awarded five times yearly. Open to couples and individuals including same-sex couples.",
    longDescription:
      "The Cozy Warrior Grant provides $5,000 grants to help individuals and couples struggling with infertility. The grant is awarded five times per year and is inclusive of all family structures including same-sex couples. Treatment must be at a clinic within the United States.",
    awardAmount: "$5,000",
    treatmentsCovered: ["IVF", "IUI"],
    eligibility: {
      location: "national",
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "infertility",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "closed",
    frequency: "5 times per year",
    howToApply:
      "Check cozywarrior.org for application openings. Applications are reviewed on a rolling basis.",
    requirements: [
      "Diagnosed infertility",
      "Treatment at U.S. clinic",
      "Open to couples and individuals",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "bef_ivf",
    name: "BEF IVF Grant",
    organization: "Boomer Esiason Foundation",
    website: "https://www.esiason.org",
    favicon: "https://www.google.com/s2/favicons?domain=esiason.org&sz=64",
    shortDescription:
      "IVF grants specifically for individuals with cystic fibrosis, ages 25–55. Available at Boston IVF or Kindbody locations.",
    longDescription:
      "The Boomer Esiason Foundation IVF Grant provides financial assistance for IVF treatments specifically for individuals diagnosed with cystic fibrosis. Applicants must be between 25 and 55 years old and U.S. citizens or permanent residents. Treatment is available at Boston IVF or Kindbody's 28 locations nationwide. Awards are given biannually.",
    awardAmount: "IVF cycle",
    treatmentsCovered: ["IVF"],
    eligibility: {
      location: "national",
      ageMin: 25,
      ageMax: 55,
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "cystic_fibrosis",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "closed",
    frequency: "Biannual",
    howToApply:
      "Check esiason.org for application openings. Treatment must be at Boston IVF or Kindbody locations.",
    requirements: [
      "Cystic fibrosis diagnosis",
      "Ages 25–55",
      "U.S. citizen or permanent resident",
      "Infertility diagnosis",
      "Treatment at Boston IVF or Kindbody",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "samfund",
    name: "SAMFund",
    organization: "Expect Miracles Foundation",
    website: "https://www.expectmiraclesfoundation.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=expectmiraclesfoundation.org&sz=64",
    shortDescription:
      "Financial assistance for young adult cancer survivors, covering post-treatment recovery including IVF costs.",
    longDescription:
      "The SAMFund, part of the Expect Miracles Foundation, provides financial support to young adult cancer survivors for post-treatment recovery needs, including fertility treatments such as IVF. The program recognizes that cancer treatment can impact fertility and aims to help survivors rebuild their lives after treatment.",
    awardAmount: "Up to $4,000",
    treatmentsCovered: ["IVF", "Egg Freezing"],
    eligibility: {
      location: "national",
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "cancer",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "closed",
    frequency: "Annual",
    howToApply:
      "Apply at expectmiraclesfoundation.org when applications open. Must provide documentation of cancer diagnosis and treatment completion.",
    requirements: [
      "Young adult cancer survivor",
      "Post-treatment recovery phase",
      "U.S. resident",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "viva",
    name: "VIVA Program",
    organization: "Bob Woodruff Foundation",
    website: "https://www.bobwoodrufffoundation.org/VIVA",
    favicon:
      "https://www.google.com/s2/favicons?domain=bobwoodrufffoundation.org&sz=64",
    shortDescription:
      "Family-building grants for wounded veterans seeking fertility assistance. No application deadlines.",
    longDescription:
      "The VIVA Program, run by the Bob Woodruff Foundation, provides family-building grants to wounded veterans. The program has no submission deadlines and distributes funds as needed. It recognizes the unique challenges that veterans face in building families after service-related injuries.",
    awardAmount: "Varies",
    treatmentsCovered: ["IVF", "IUI", "Donor Egg", "Surrogacy"],
    eligibility: {
      location: "national",
      insuranceRequired: "any",
      citizenshipRequired: "us_citizen",
      maritalStatus: "any",
      diagnosis: "any",
      specialGroups: "veterans",
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "open",
    frequency: "Rolling (no deadlines)",
    howToApply:
      "Apply through bobwoodrufffoundation.org/VIVA. No submission deadlines; funds distributed as needed.",
    requirements: ["Wounded veteran", "Seeking family-building assistance"],
    lastVerified: "2026-03-18",
  },
  {
    id: "men_having_babies",
    name: "Men Having Babies",
    organization: "Men Having Babies",
    website: "https://www.menhavingbabies.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=menhavingbabies.org&sz=64",
    shortDescription:
      "Cash grants and provider discounts for LGBTQ+ prospective parents pursuing surrogacy and fertility treatments.",
    longDescription:
      "Men Having Babies provides cash grants and maintains a directory of providers offering discounts to LGBTQ+ prospective parents. The organization focuses on making surrogacy and assisted reproduction more accessible to the LGBTQ+ community through financial assistance, education, and advocacy.",
    awardAmount: "Varies",
    treatmentsCovered: ["IVF", "Surrogacy", "Donor Egg"],
    eligibility: {
      location: "national",
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "any",
      specialGroups: "lgbtq",
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "open",
    frequency: "Rolling",
    howToApply:
      "Apply at menhavingbabies.org. Access the provider directory for clinics offering LGBTQ+ discounts.",
    requirements: [
      "LGBTQ+ prospective parent",
      "Approved through application process",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "footsteps_fertility",
    name: "Footsteps for Fertility",
    organization: "Footsteps for Fertility Foundation",
    website: "https://www.footstepsforfertility.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=footstepsforfertility.org&sz=64",
    shortDescription:
      "Cash grants of $3,500–$10,000 or in-kind grants covering a full IVF cycle for U.S. residents with documented infertility.",
    longDescription:
      "Footsteps for Fertility Foundation provides both cash grants ranging from $3,500 to $10,000 and in-kind grants that cover one full IVF cycle. Applicants must have documented infertility and be medically cleared for treatment. The foundation aims to make fertility treatment accessible to those who cannot afford it.",
    awardAmount: "$3,500–$10,000",
    treatmentsCovered: ["IVF"],
    eligibility: {
      location: "national",
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "infertility",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "open",
    frequency: "Rolling",
    howToApply:
      "Apply online at footstepsforfertility.org. Requires documentation of infertility diagnosis and medical clearance.",
    requirements: [
      "U.S. resident",
      "Documented infertility diagnosis",
      "Medically cleared for treatment",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "nest_egg",
    name: "Nest Egg Foundation",
    organization: "Nest Egg Foundation",
    website: "https://www.nesteggfoundation.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=nesteggfoundation.org&sz=64",
    shortDescription:
      "$5,000–$20,000 grants for IVF, cryopreservation, and donor cycles. Connecticut and New York residents only.",
    longDescription:
      "The Nest Egg Foundation provides grants typically ranging from $5,000 to $20,000 for fertility treatments including IVF, egg/sperm cryopreservation, embryo testing, and donor cycles. The foundation serves residents of Connecticut and New York who have documented infertility.",
    awardAmount: "$5,000–$20,000",
    treatmentsCovered: ["IVF", "Egg Freezing", "Donor Egg"],
    eligibility: {
      location: ["CT", "NY"],
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "infertility",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "open",
    frequency: "Rolling",
    howToApply:
      "Apply online at nesteggfoundation.org. Must be a Connecticut or New York resident with documented infertility.",
    requirements: [
      "Resident of Connecticut or New York",
      "Documented infertility diagnosis",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "surrogacy_foundation",
    name: "The Surrogacy Foundation",
    organization: "The Surrogacy Foundation",
    website: "https://www.thesurrogacyfoundation.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=thesurrogacyfoundation.org&sz=64",
    shortDescription:
      "One of the largest surrogacy grants available: up to $100,000 covering nearly the entire surrogacy journey.",
    longDescription:
      "The Surrogacy Foundation, an Atlanta-based nonprofit, offers one of the nation's largest surrogacy grants at $100,000 per recipient. The grant covers nearly the entire surrogacy journey. Applicants must demonstrate medical necessity, reside in the U.S., and have at least three cryopreserved euploid embryos. The foundation has awarded over $500,000 in lifetime direct financial support.",
    awardAmount: "Up to $100,000",
    treatmentsCovered: ["Surrogacy"],
    eligibility: {
      location: "national",
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "any",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "open",
    frequency: "Annual",
    howToApply:
      "Apply at thesurrogacyfoundation.org. Multi-stage application and review process.",
    requirements: [
      "U.S. resident",
      "Medical necessity for surrogacy",
      "At least 3 cryopreserved euploid embryos",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "livestrong_fertility",
    name: "LIVESTRONG Fertility",
    organization: "LIVESTRONG Foundation",
    website: "https://www.livestrong.org/what-we-do/program/fertility",
    favicon: "https://www.google.com/s2/favicons?domain=livestrong.org&sz=64",
    shortDescription:
      "Discounted fertility preservation services and donated medications for cancer patients facing treatment that may affect fertility.",
    longDescription:
      "LIVESTRONG Fertility connects cancer patients with fertility preservation services at reduced or no cost. The program provides discounted services and donated medications through a network of partner clinics and pharmaceutical companies. It helps both men and women preserve their fertility before cancer treatment begins.",
    awardAmount: "Discounted/free services",
    treatmentsCovered: ["Egg Freezing", "IVF"],
    eligibility: {
      location: "national",
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "cancer",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "open",
    frequency: "Rolling",
    howToApply:
      "Apply at livestrong.org/fertility. The program connects patients with participating fertility clinics offering discounted services.",
    requirements: [
      "Cancer diagnosis",
      "Need for fertility preservation before treatment",
      "U.S. resident",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "chick_mission",
    name: "The Chick Mission",
    organization: "The Chick Mission",
    website: "https://www.thechickmission.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=thechickmission.org&sz=64",
    shortDescription:
      "Hope Grants for newly diagnosed female cancer patients to cover egg or embryo freezing costs.",
    longDescription:
      "The Chick Mission provides Hope Grants to newly diagnosed female cancer patients to help cover the costs of egg or embryo freezing. The organization recognizes that a cancer diagnosis often comes with urgent fertility preservation needs and aims to remove the financial barrier to preserving future family-building options.",
    awardAmount: "Varies",
    treatmentsCovered: ["Egg Freezing"],
    eligibility: {
      location: "national",
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "cancer",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "open",
    frequency: "Rolling",
    howToApply: "Apply for a Hope Grant at thechickmission.org/hope-grants.",
    requirements: [
      "Newly diagnosed female cancer patient",
      "Need for egg or embryo freezing",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "team_maggies_dream",
    name: "Team Maggie's Dream",
    organization: "Team Maggie's Dream Foundation",
    website: "https://www.teammaggiesdream.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=teammaggiesdream.org&sz=64",
    shortDescription:
      "Up to $2,500 for fertility preservation for young adults ages 15–37 diagnosed with cancer.",
    longDescription:
      "Team Maggie's Dream Foundation provides grants of up to $2,500 to support fertility preservation for young adults between ages 15 and 37 who have been diagnosed with cancer. The foundation helps cover costs associated with egg freezing, sperm banking, and other fertility preservation methods.",
    awardAmount: "Up to $2,500",
    treatmentsCovered: ["Egg Freezing"],
    eligibility: {
      location: "national",
      ageMin: 15,
      ageMax: 37,
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "cancer",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "open",
    frequency: "Rolling",
    howToApply:
      "Apply at teammaggiesdream.org/grants. Must provide documentation of cancer diagnosis.",
    requirements: [
      "Ages 15–37",
      "Cancer diagnosis",
      "Need for fertility preservation",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "chicago_coalition",
    name: "Coalition for Family Building",
    organization: "Chicago Coalition for Family Building",
    website: "https://coalitionforfamilybuilding.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=coalitionforfamilybuilding.org&sz=64",
    shortDescription:
      "IVF cycle grants or up to $10,000 financial grants for patients seeking treatment in IL, WI, IA, IN, or MO.",
    longDescription:
      "The Chicago Coalition for Family Building offers IVF cycle grants or financial grants of up to $10,000 for individuals and couples seeking fertility treatment in Illinois, Wisconsin, Iowa, Indiana, or Missouri. The coalition also offers specialized Magnolia Tree Grants for BRCA mutation carriers.",
    awardAmount: "Up to $10,000",
    treatmentsCovered: ["IVF", "Egg Freezing"],
    eligibility: {
      location: ["IL", "WI", "IA", "IN", "MO"],
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "infertility",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "open",
    frequency: "Biannual (April & December)",
    howToApply:
      "Apply at coalitionforfamilybuilding.org during open application cycles.",
    requirements: [
      "Treatment in IL, WI, IA, IN, or MO",
      "Infertility diagnosis",
    ],
    lastVerified: "2026-03-18",
  },
  {
    id: "fertility_within_reach",
    name: "Fertility Within Reach",
    organization: "Fertility Within Reach",
    website: "https://www.fertilitywithinreach.org",
    favicon:
      "https://www.google.com/s2/favicons?domain=fertilitywithinreach.org&sz=64",
    shortDescription:
      "$500–$2,000 grants for Massachusetts residents to help cover general fertility treatment costs.",
    longDescription:
      "Fertility Within Reach provides grants ranging from $500 to $2,000 to Massachusetts residents to help offset the costs of fertility treatments. While the grant amounts are smaller, the organization serves as an important resource for those in the early stages of fertility treatment or those who need help covering co-pays and medication costs.",
    awardAmount: "$500–$2,000",
    treatmentsCovered: ["IVF", "IUI"],
    eligibility: {
      location: ["MA"],
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "infertility",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "open",
    frequency: "Rolling",
    howToApply:
      "Apply at fertilitywithinreach.org. Must be a Massachusetts resident.",
    requirements: ["Massachusetts resident", "Infertility diagnosis"],
    lastVerified: "2026-03-18",
  },
  {
    id: "endofound",
    name: "Endometriosis Foundation",
    organization: "Endometriosis Foundation of America",
    website: "https://www.endofound.org",
    favicon: "https://www.google.com/s2/favicons?domain=endofound.org&sz=64",
    shortDescription:
      "Partial coverage for diagnostic and fertility-related expenses for individuals diagnosed with endometriosis.",
    longDescription:
      "The Endometriosis Foundation of America provides financial assistance through partnerships to help cover diagnostic and fertility-related expenses for individuals diagnosed with endometriosis. The foundation connects patients with resources and participating clinics that offer reduced-cost services.",
    awardAmount: "Varies",
    treatmentsCovered: ["IVF", "IUI"],
    eligibility: {
      location: "national",
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "endometriosis",
      specialGroups: null,
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "open",
    frequency: "Rolling",
    howToApply:
      "Visit endofound.org for partnership programs and financial assistance options.",
    requirements: ["Endometriosis diagnosis", "U.S. resident"],
    lastVerified: "2026-03-18",
  },
  {
    id: "hasidah",
    name: "Hasidah",
    organization: "Hasidah",
    website: "https://www.hasidah.org",
    favicon: "https://www.google.com/s2/favicons?domain=hasidah.org&sz=64",
    shortDescription:
      "$5,000–$15,000 IVF grants for Jewish families with medical and financial need.",
    longDescription:
      "Hasidah provides IVF grants ranging from $5,000 to $15,000 to Jewish families experiencing medical and financial need. The organization supports families across various Jewish denominations and backgrounds, helping them access fertility treatments that might otherwise be financially out of reach.",
    awardAmount: "$5,000–$15,000",
    treatmentsCovered: ["IVF", "IUI"],
    eligibility: {
      location: "national",
      insuranceRequired: "any",
      citizenshipRequired: "us_resident",
      maritalStatus: "any",
      diagnosis: "infertility",
      specialGroups: "jewish",
    },
    applicationFee: null,
    applicationDeadline: null,
    applicationStatus: "open",
    frequency: "Rolling",
    howToApply: "Apply at hasidah.org. At least one parent must be Jewish.",
    requirements: [
      "At least one parent identifies as Jewish",
      "Medical need for fertility treatment",
      "Financial need demonstrated",
    ],
    lastVerified: "2026-03-18",
  },
];
